// Carrousel de bienvenue, montré une fois aux comptes qui n'ont jamais été
// clients.
//
// Web : bienvenue → fonctionnement → offre gratuite → premium → essai gratuit.
// App native : pas de diapositive d'offre (règles des stores).
//
// Rendu masqué ([hidden]) ; le hook useWelcomeCarousel l'ouvre (automatiquement
// ou par l'événement northvane:welcome:open) et le pilote. Les <div> du haut et
// du bas ne sont pas des <header>/<footer> : le site masque tous les <header>
// dans l'app et tous les <footer> jusqu'à 1024 px, avec !important.
import { t } from '@/lib/i18n'
import { useShared } from '@/hooks/useShared'
import { useWelcomeCarousel } from '@/hooks/useWelcomeCarousel'
import PricingPlans from '@/components/billing/PricingPlans'
import type { WelcomeData, WelcomeSlide } from './welcome/types'

const W = 'welcome_onboarding'

const HOW_STEPS = [
  ['1', 'fa-plus'],
  ['2', 'fa-wand-magic-sparkles'],
  ['3', 'fa-bolt'],
] as const

const PREMIUM_FEATURES = [
  ['predictions', 'fa-crosshairs', 'starter'],
  ['targets', 'fa-bullseye', 'starter'],
  ['simulations', 'fa-rocket', 'pro'],
  ['charts', 'fa-chart-area', 'pro'],
  ['assistant', 'fa-robot', 'pro'],
  ['alerts', 'fa-bell', 'pro'],
] as const

export default function Welcome({ data: rawData }: { data: Record<string, any> }) {
  const data = rawData as WelcomeData
  const { auth } = useShared()
  const slides = data.slides
  const total = slides.length
  const carousel = useWelcomeCarousel({
    autoOpen: data.autoOpen,
    seenUrl: data.seenUrl,
    closedUrl: data.closedUrl,
    slides,
    total,
    userKey: String(auth.user?.id ?? ''),
  })
  const { nav } = carousel

  const titles: Record<WelcomeSlide, string> = {
    welcome: data.firstName ? t(`${W}.welcome.title`, { name: data.firstName }) : t(`${W}.welcome.title_anonymous`),
    how: t(`${W}.how.title`),
    free: t(`${W}.free.title`),
    premium: t(`${W}.premium.title`),
    offer: t(`${W}.offer.title`),
  }

  const index = nav?.index ?? 0
  const current = slides[index]
  const isOffer = nav !== null && current === 'offer'
  const isLast = index === total - 1
  const nextIsOffer = slides[index + 1] === 'offer'
  const nextLabel = nav === null || index === 0 ? t(`${W}.start`) : nextIsOffer ? t(`${W}.see_offer`) : isLast ? t(`${W}.finish`) : t(`${W}.next`)

  // Diapositive : masquée et inerte sauf l'active, une fois le carrousel lancé.
  const slideProps = (name: WelcomeSlide, extraClass = '') => {
    const i = slides.indexOf(name)
    const active = nav === null || i === nav.index
    const animation = nav !== null && active ? nav.animation : ''
    return {
      className: ['nv-welcome__slide', extraClass, animation].filter(Boolean).join(' '),
      'data-slide': name,
      'aria-roledescription': 'slide',
      hidden: !active,
      inert: !active,
    }
  }

  const rootClass = ['nv-welcome', carousel.openClass && 'is-open', isOffer && 'is-offer'].filter(Boolean).join(' ')

  return (
    <div
      id="nv-welcome"
      className={rootClass}
      hidden={!carousel.visible}
      data-welcome-onboarding-auto-open-value={String(carousel.autoOpen)}
    >
      <div className="nv-welcome__backdrop" onClick={() => carousel.close('dismiss')}></div>

      <div
        className="nv-welcome__dialog"
        role="dialog"
        aria-modal="true"
        aria-label={nav === null ? t(`${W}.aria_label`) : titles[current]}
        tabIndex={-1}
        ref={carousel.dialogRef}
        onTouchStart={carousel.onTouchStart}
        onTouchEnd={carousel.onTouchEnd}
      >
        <div className="nv-welcome__progress" aria-hidden="true">
          <span className="nv-welcome__progress-bar" style={nav === null ? undefined : { width: `${((index + 1) / total) * 100}%` }}></span>
        </div>

        <div className="nv-welcome__top">
          <span className="nv-welcome__counter" aria-live="polite">
            {nav === null ? '' : t(`${W}.step_counter`, { current: index + 1, total })}
          </span>
          <div className="nv-welcome__top-actions">
            <button type="button" className="nv-welcome__skip" onClick={() => carousel.close('skip')}>
              {t(`${W}.skip`)}
            </button>
            <button type="button" className="nv-welcome__close" aria-label={t(`${W}.close`)} onClick={() => carousel.close('dismiss')}>
              <i className="fas fa-xmark" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <div className="nv-welcome__viewport">
          <div className="nv-welcome__track">
            {/* 1. Bienvenue */}
            <section {...slideProps('welcome', 'nv-welcome__slide--hero')}>
              <div className="nv-welcome__visual nv-welcome__visual--orbit" aria-hidden="true">
                <span className="nv-welcome__ring nv-welcome__ring--1"></span>
                <span className="nv-welcome__ring nv-welcome__ring--2"></span>
                <span className="nv-welcome__ring nv-welcome__ring--3"></span>
                <img src="/images/Logo_transparent.png" alt="" className="nv-welcome__logo" width="84" height="84" />
              </div>
              <p className="nv-welcome__eyebrow">{t(`${W}.welcome.eyebrow`)}</p>
              <h2 className="nv-welcome__title">{titles.welcome}</h2>
              <p className="nv-welcome__lede">{t(`${W}.welcome.subtitle`)}</p>
              <ul className="nv-welcome__chips">
                <li><i className="fas fa-chart-line" aria-hidden="true"></i>{t(`${W}.welcome.chip_1`)}</li>
                <li><i className="fas fa-signal" aria-hidden="true"></i>{t(`${W}.welcome.chip_2`)}</li>
                <li><i className="fas fa-bell" aria-hidden="true"></i>{t(`${W}.welcome.chip_3`)}</li>
              </ul>
              <p className="nv-welcome__hint"><i className="far fa-clock" aria-hidden="true"></i> {t(`${W}.welcome.duration`)}</p>
            </section>

            {/* 2. Fonctionnement */}
            <section {...slideProps('how')}>
              <p className="nv-welcome__eyebrow">{t(`${W}.how.eyebrow`)}</p>
              <h2 className="nv-welcome__title">{titles.how}</h2>
              <ol className="nv-welcome__steps">
                {HOW_STEPS.map(([n, icon]) => (
                  <li key={n} className="nv-welcome__step-card">
                    <span className="nv-welcome__step-index">{n}</span>
                    <span className="nv-welcome__step-icon"><i className={`fas ${icon}`} aria-hidden="true"></i></span>
                    <strong>{t(`${W}.how.step_${n}_title`)}</strong>
                    <span>{t(`${W}.how.step_${n}_text`, { n: data.freeStocks })}</span>
                  </li>
                ))}
              </ol>
            </section>

            {/* 3. Offre gratuite */}
            <section {...slideProps('free')}>
              <p className="nv-welcome__eyebrow nv-welcome__eyebrow--free"><i className="fas fa-circle-check" aria-hidden="true"></i> {t(`${W}.free.eyebrow`)}</p>
              <h2 className="nv-welcome__title">{titles.free}</h2>
              <ul className="nv-welcome__checklist">
                <li><i className="fas fa-check" aria-hidden="true"></i>{t(`${W}.free.item_stocks`, { n: data.freeStocks })}</li>
                <li><i className="fas fa-check" aria-hidden="true"></i>{t(`${W}.free.item_refresh`)}</li>
                <li><i className="fas fa-check" aria-hidden="true"></i>{t(`${W}.free.item_opportunities`)}</li>
                <li><i className="fas fa-check" aria-hidden="true"></i>{t(`${W}.free.item_fundamentals`)}</li>
                <li><i className="fas fa-check" aria-hidden="true"></i>{t(`${W}.free.item_markets`)}</li>
              </ul>
              <p className="nv-welcome__note"><i className="fas fa-lock-open" aria-hidden="true"></i> {t(`${W}.free.note`)}</p>
            </section>

            {/* 4. Premium */}
            <section {...slideProps('premium')}>
              <p className="nv-welcome__eyebrow nv-welcome__eyebrow--premium"><i className="fas fa-crown" aria-hidden="true"></i> {t(`${W}.premium.eyebrow`)}</p>
              <h2 className="nv-welcome__title">{titles.premium}</h2>
              <p className="nv-welcome__lede nv-welcome__lede--tight">{t(`${W}.premium.subtitle`)}</p>
              <div className="nv-welcome__features">
                {PREMIUM_FEATURES.map(([key, icon, tier]) => (
                  <div key={key} className="nv-welcome__feature">
                    <span className="nv-welcome__feature-icon"><i className={`fas ${icon}`} aria-hidden="true"></i></span>
                    <div>
                      <strong>{t(`${W}.premium.f_${key}`)} <span className={`nv-welcome__tag nv-welcome__tag--${tier}`}>{t(`${W}.premium.tag_${tier}`)}</span></strong>
                      <span>{t(`${W}.premium.f_${key}_desc`)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="nv-welcome__note"><i className="fas fa-layer-group" aria-hidden="true"></i> {t(`${W}.premium.more_room`, { stocks: data.maxStocks })}</p>
            </section>

            {/* 5. Offre (web seulement) */}
            {data.pricing && (
              <section {...slideProps('offer', 'nv-welcome__slide--offer')}>
                <p className="nv-welcome__eyebrow nv-welcome__eyebrow--trial"><i className="fas fa-gift" aria-hidden="true"></i> {t(`${W}.offer.eyebrow`)}</p>
                <h2 className="nv-welcome__title">{titles.offer}</h2>
                <p className="nv-welcome__lede nv-welcome__lede--tight">{t(`${W}.offer.subtitle`)}</p>

                {/* La même grille que /pricing et la landing : jamais un autre prix.
                    Un plan choisi part en POST natif vers le paiement : noté avant. */}
                <div className="nv-welcome__pricing" onSubmitCapture={() => carousel.track('plan')}>
                  <PricingPlans data={data.pricing} />
                </div>

                <div className="nv-welcome__stay-free">
                  <button type="button" className="nv-welcome__btn nv-welcome__btn--ghost" onClick={() => carousel.close('free')}>
                    {t(`${W}.offer.stay_free`)}
                  </button>
                  <p>{t(`${W}.offer.stay_free_note`)}</p>
                </div>

                <ul className="nv-welcome__trust">
                  <li><i className="fas fa-shield-halved" aria-hidden="true"></i>{t(`${W}.offer.trust_1`)}</li>
                  <li><i className="fas fa-envelope-open-text" aria-hidden="true"></i>{t(`${W}.offer.trust_2`)}</li>
                  <li><i className="fas fa-rotate-left" aria-hidden="true"></i>{t(`${W}.offer.trust_3`)}</li>
                </ul>
                <p className="nv-welcome__card-note">{t(`${W}.offer.card_note`)}</p>
              </section>
            )}
          </div>
        </div>

        <div className="nv-welcome__nav">
          <button
            type="button"
            className="nv-welcome__nav-back"
            aria-label={t(`${W}.back`)}
            style={nav === null ? undefined : { visibility: index === 0 ? 'hidden' : 'visible' }}
            onClick={carousel.previous}
          >
            <i className="fas fa-arrow-left" aria-hidden="true"></i>
          </button>
          <div className="nv-welcome__dots" role="tablist">
            {slides.map((name, i) => (
              <button
                key={name}
                type="button"
                className={`nv-welcome__dot${nav !== null && i === index ? ' is-active' : ''}`}
                role="tab"
                data-index={i}
                aria-label={t(`${W}.step_counter`, { current: i + 1, total })}
                aria-selected={nav === null ? undefined : i === index ? 'true' : 'false'}
                onClick={() => carousel.goTo(i)}
              ></button>
            ))}
          </div>
          <button
            type="button"
            className="nv-welcome__btn nv-welcome__btn--primary nv-welcome__nav-next"
            hidden={isOffer}
            data-label-start={t(`${W}.start`)}
            data-label-next={t(`${W}.next`)}
            data-label-offer={t(`${W}.see_offer`)}
            data-label-finish={t(`${W}.finish`)}
            onClick={carousel.next}
          >
            {nextLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
